'use client';

import { useState, type FormEvent } from 'react';
import { uiFb } from '@/lib/i18n-helpers';

interface SubscribeFormProps {
  lang?: string;
  source?: string;
  countryCode?: string;
}

export function SubscribeForm({ lang = 'en', source = 'footer', countryCode }: SubscribeFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      let ref_agent: string | null = null;
      let ref_campaign: string | null = null;
      try {
        ref_agent = sessionStorage.getItem('ref_agent');
        ref_campaign = sessionStorage.getItem('ref_campaign');
      } catch { /* SSR/privacy */ }

      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          source,
          country_detected: countryCode || null,
          ref_agent,
          ref_campaign,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to subscribe');
      }

      setStatus('success');
      setEmail('');

      // Track in Matomo
      if (typeof window !== 'undefined') {
        window._paq?.push(['trackEvent', 'Subscribe', 'Email', window.location.pathname]);
        window._paq?.push(['trackGoal', 1]);
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to subscribe');
    }
  }

  if (status === 'success') {
    return (
      <p className="text-sm text-semantic-success">
        {uiFb('ui.subscribe.success', 'Thanks for subscribing! We\'ll keep you updated.', lang)}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={uiFb('ui.subscribe.placeholder', 'your@email.com', lang)}
        aria-label={uiFb('ui.subscribe.label', 'Email address', lang)}
        className="min-w-0 flex-1 rounded-lg border border-border-subtle bg-surface-root px-3 py-2 text-sm text-text-primary placeholder:text-text-dim focus:border-brand focus:outline-none"
        disabled={status === 'loading'}
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="shrink-0 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-text-on-brand transition-colors hover:bg-brand/90 disabled:opacity-50"
      >
        {status === 'loading'
          ? uiFb('ui.subscribe.sending', 'Sending…', lang)
          : uiFb('ui.subscribe.button', 'Subscribe', lang)}
      </button>
      {status === 'error' && (
        <p className="absolute mt-10 text-xs text-semantic-error">{errorMsg}</p>
      )}
    </form>
  );
}
